import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { SiteReportService, EventData } from '../../service/site-report.service';
import { ToastrService } from 'ngx-toastr';
import { SpinnerService } from '../../../spinner-component/spinner.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';

export interface SiteReportModalData {
  siteName: string;
}

@Component({
  selector: 'app-site-report-modal',
  templateUrl: './site-report-modal.component.html',
  styleUrls: ['./site-report-modal.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule
  ]
})
export class SiteReportModalComponent implements OnInit {
  reportForm: FormGroup;
  isLoading = false;
  showDashboard = false;
  currentReportData: EventData[] = [];
  currentSiteName = '';
  isFullScreen = false;

  constructor(
    private dialogRef: MatDialogRef<SiteReportModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SiteReportModalData,
    private fb: FormBuilder,
    private siteReportService: SiteReportService,
    private toaster: ToastrService,
    private spinner: SpinnerService
  ) {
    this.reportForm = this.fb.group({
      fromDate: ['', Validators.required],
      toDate: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    // Set default dates to August 2025 (the working date range)
    const fromDate = '2025-08-01';
    const toDate = '2025-08-31';
    
    this.reportForm.patchValue({
      fromDate: fromDate,
      toDate: toDate
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onGenerateReport(): void {
    if (this.reportForm.valid) {
      const formValue = this.reportForm.value;
      this.generateReport(this.data.siteName, formValue.fromDate, formValue.toDate);
    }
  }

  private generateReport(siteName: string, fromDate: string, toDate: string): void {
    this.isLoading = true;
    this.spinner.activate();
    this.toaster.info('Downloading site report data...');

    this.siteReportService.downloadSiteReport(siteName, fromDate, toDate).subscribe({
      next: async (csvText: string) => {
        try {
          if (!csvText || csvText.trim() === '') {
            this.spinner.deactivate();
            this.toaster.warning('No data found for the selected site and date range.');
            this.isLoading = false;
            return;
          }

          const eventData = await this.siteReportService.parseCSVData(csvText);
          
          if (eventData.length === 0) {
            this.spinner.deactivate();
            this.toaster.warning('No data found for the selected date range.');
            this.isLoading = false;
            return;
          }

          this.currentReportData = eventData;
          this.currentSiteName = siteName;
          this.showDashboard = true;
          
          this.spinner.deactivate();
          
          // Wait for charts to be ready before showing success message
          setTimeout(() => {
            this.toaster.success(`Successfully loaded ${eventData.length} events for ${siteName}`);
          }, 1500); // Wait 1.5 seconds for charts to render
          
        } catch (error) {
          this.spinner.deactivate();
          this.toaster.error('Failed to process CSV data. Please check the file format.');
          this.isLoading = false;
        }
      },
      error: (error) => {
        this.spinner.deactivate();
        this.isLoading = false;
        console.error('Site report download error:', error);
        
        if (error.status === 404) {
          this.toaster.error('No data found for the selected site and date range.');
        } else if (error.status === 403) {
          this.toaster.error('Access denied. Please check your permissions.');
        } else if (error.status === 0) {
          this.toaster.error('Network error. Please check your connection.');
        } else {
          this.toaster.error(`Failed to download site report (${error.status}). Please try again.`);
        }
      }
    });
  }

  closeDashboard(): void {
    this.showDashboard = false;
    this.currentReportData = [];
    this.currentSiteName = '';
  }

  toggleFullScreen(): void {
    this.isFullScreen = !this.isFullScreen;
    
    // Get the dialog container and apply full screen class
    setTimeout(() => {
      const dialogContainer = document.querySelector('.site-report-modal');
      const dialogOverlay = document.querySelector('.cdk-overlay-pane');
      
      if (dialogContainer) {
        if (this.isFullScreen) {
          dialogContainer.classList.add('fullscreen');
        } else {
          dialogContainer.classList.remove('fullscreen');
        }
      }
      
      if (dialogOverlay) {
        if (this.isFullScreen) {
          dialogOverlay.classList.add('fullscreen');
        } else {
          dialogOverlay.classList.remove('fullscreen');
        }
      }
    }, 100);
  }
}
